/**
 * How tall an image may lay out before it counts as long enough to clip, in px.
 *
 * This is the decision only. How much of a clipped image stays visible is MEDIA_CLIP_HEIGHT, and the
 * two are deliberately different numbers: what makes a card too long to scroll past is not the same
 * question as how much of a long picture is worth showing before the reveal.
 */
export const MEDIA_TALL_THRESHOLD = 420;

/**
 * How much of a clipped image stays visible, in px. Lower than the threshold on purpose - once an
 * image is being cut back, a short window plus the reveal is tidier than a nearly-full-height one,
 * and it is enough to recognise a briefing graphic by its header.
 */
export const MEDIA_CLIP_HEIGHT = 280;

/**
 * How much taller than wide an image has to be before clipping it is even considered.
 *
 * This is the part that decides whether a picture is "long", and MEDIA_TALL_THRESHOLD only decides
 * how long is too long. Raising the threshold instead was the wrong lever: it spares ordinary images
 * by also sparing the briefing graphics this exists for.
 *
 * The case that motivated it: a square image in a 450px column lays out 450px tall, cleared a 420px
 * threshold by 28px, and got cropped - which reads as cropping a perfectly normal picture for no
 * reason. Anything square or landscape is now left alone at any column width, however wide the feed
 * gets.
 */
const MIN_ASPECT = 1.25;

/**
 * Whether an image should be clipped: clearly portrait, *and* long enough in this column to run the
 * card off the screen.
 *
 * The card sizes images to the full width of the column and lets the height follow the aspect ratio,
 * so a portrait image makes an arbitrarily long card. CSS alone cannot tell us whether a given image
 * is one of those: `max-height` clips it either way, and the reveal button would then appear on every
 * post. So the decision needs the natural dimensions, which are only known once the image has
 * loaded - hence measuring in `onLoad` rather than styling it away.
 *
 * Returns false for anything unmeasurable, so an image with dimensions we cannot read renders
 * uncapped rather than clipped with no way to open it.
 */
export function isMediaTall(
    naturalWidth: number,
    naturalHeight: number,
    renderedWidth: number,
    threshold: number = MEDIA_TALL_THRESHOLD,
): boolean {
    if (! naturalWidth || ! naturalHeight || ! renderedWidth) {
        return false;
    }
    const aspect = naturalHeight / naturalWidth;
    if (aspect < MIN_ASPECT) {
        return false;
    }
    return aspect * renderedWidth > threshold;
}
